import React from "react";
import { Link } from "wouter";
import { Course } from "@workspace/api-client-react";
import { Badge } from "@/components/ui/badge";
import { Clock, Video, Users, ArrowUpRight, BookOpen } from "lucide-react";
import { formatPriceCents } from "@/lib/format";
import { motion } from "framer-motion";
import { fadeUp } from "@/lib/motion";

const TOPIC_ACCENT: Record<string, { bar: string; chip: string; label: string }> = {
  python: { bar: "bg-primary", chip: "bg-primary/10 text-primary", label: "PYTHON" },
  vibecoding: { bar: "bg-secondary", chip: "bg-secondary/30 text-foreground", label: "VIBECODING" },
  ai: { bar: "bg-accent", chip: "bg-accent/15 text-accent", label: "AI" },
};

export function CourseCard({ course }: { course: Course }) {
  const accent = TOPIC_ACCENT[course.topic] ?? TOPIC_ACCENT.python;
  const isLive = course.format === "live";

  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      className="h-full group/card"
    >
      <Link
        href={`/courses/${course.slug}`}
        className="flex flex-col h-full bg-card border border-border rounded-xl overflow-hidden shadow-sm transition-shadow duration-300 group-hover/card:shadow-xl relative"
      >
        {/* Topic accent bar */}
        <div className={`h-1.5 ${accent.bar}`} aria-hidden="true" />

        {/* Cover */}
        <div className="relative h-44 overflow-hidden bg-muted">
          {course.coverImageUrl ? (
            <img
              src={course.coverImageUrl}
              alt=""
              className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-[1.04]"
              loading="lazy"
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${accent.chip}`}>
              <span className="font-display text-5xl font-bold opacity-30">{accent.label}</span>
            </div>
          )}
          <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 bg-background/95 backdrop-blur-sm text-foreground rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border border-border shadow-sm">
              {isLive ? <Users className="w-3 h-3" /> : <Video className="w-3 h-3" />}
              {isLive ? "Live · Zoom" : "Self-paced"}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest bg-foreground/85 text-background backdrop-blur-sm rounded-full px-2.5 py-1">
              {course.level === "middle" ? "6–8" : "9–12"}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 flex-1 flex flex-col">
          <div className="flex items-baseline justify-between gap-2 mb-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              [{accent.label}] · {String(course.lessonCount).padStart(2, "0")} lessons
            </span>
            <span className="font-mono text-sm font-semibold tabular-nums">
              {formatPriceCents(course.priceCents)}
            </span>
          </div>
          <h3 className="font-display text-2xl font-bold leading-[1.1] tracking-tight mb-2 group-hover/card:text-primary transition-colors text-balance">
            {course.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-5">
            {course.tagline}
          </p>

          <div className="mt-auto pt-4 border-t border-dashed border-border flex items-center justify-between text-xs font-mono uppercase tracking-wider text-muted-foreground">
            <span className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <BookOpen className="w-3.5 h-3.5" /> {course.lessonCount}
              </span>
              {isLive && course.durationWeeks && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {course.durationWeeks}w
                </span>
              )}
            </span>
            <span className="inline-flex items-center gap-1 text-foreground font-bold group-hover/card:text-primary transition-colors">
              Open <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover/card:translate-x-0.5 group-hover/card:-translate-y-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
